import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Button,
  ClickAwayListener,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import cx from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Guide from "components/Guide";
import ShotContext from "components/Shot/ShotContext";
import { SERVICES, UserGuides } from "constants/constants";
import { SLAPSHOT_HOME_URL } from "constants/urls";
import { getServiceDisplayName } from "utils/helper";

import styles from "./ServiceDropdown.module.scss";

type ServiceState = {
  visible: SERVICES[];
  usage: SERVICES[];
};

// retime service is deprecated
const ALL_SERVICES = Object.values(SERVICES).filter(
  (s) => s !== SERVICES.RETIME,
) as SERVICES[];

const DEFAULT_SERVICES_ORDER: SERVICES[] = [
  SERVICES.ROTOSCOPING,
  SERVICES.TRACKING,
  SERVICES.SMART_VECTORS,
];

const SERVICE_BADGES: Partial<Record<SERVICES, string>> = {
  [SERVICES.TRACKING]: "BETA",
  [SERVICES.SMART_VECTORS]: "NEW",
};

const selectService = (selectedService: SERVICES, state: ServiceState) => {
  const isVisible = state.visible.includes(selectedService);

  if (isVisible) {
    // Service is already one of the three buttons: only update recency.
    const nextUsage = [
      selectedService,
      ...state.usage.filter((s) => s !== selectedService),
    ];

    return {
      visible: state.visible,
      usage: nextUsage,
    };
  }

  // Service is not visible: evict the least recently used visible service.
  const lruService = state.usage[state.usage.length - 1];
  const indexToReplace = state.visible.indexOf(lruService);

  const nextVisible = [...state.visible];

  if (indexToReplace === -1) {
    // Fallback: replace the last visible slot
    nextVisible[nextVisible.length - 1] = selectedService;
  } else {
    nextVisible[indexToReplace] = selectedService;
  }

  const nextUsage = [
    selectedService,
    ...state.usage.filter((s) => s !== selectedService && s !== lruService),
  ];

  return {
    visible: nextVisible,
    usage: nextUsage,
  };
};

const ServiceDropdown = () => {
  const { service, setService, layerId, project, shotId } =
    useContext(ShotContext);
  const { serviceParam } = useParams();
  const navigate = useNavigate();

  const [serviceState, setServiceState] = useState<ServiceState>(() => ({
    visible: [...DEFAULT_SERVICES_ORDER],
    usage: [...DEFAULT_SERVICES_ORDER],
  }));

  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSelection, setDropdownSelection] = useState<string>("");

  const remainingServices = ALL_SERVICES.filter(
    (s) => !serviceState.visible.includes(s),
  );

  const handleSelectService = useCallback(
    (selectedService: string) => {
      if (layerId) {
        navigate(
          `${SLAPSHOT_HOME_URL}projects/${project.id}/${selectedService}/shots/${shotId}`,
        );
      } else if (window.location.pathname.includes(service)) {
        navigate(window.location.pathname.replace(service, selectedService));
      } else if (
        (service === SERVICES.DEPTH_MAP ||
          service === SERVICES.SMART_VECTORS) &&
        shotId
      ) {
        navigate(
          `${SLAPSHOT_HOME_URL}projects/${project.id}/${selectedService}/shots/${shotId}`,
        );
      } else {
        navigate(
          `${SLAPSHOT_HOME_URL}projects/${project.id}/${selectedService}`,
        );
      }
    },
    [layerId, navigate, project, service, shotId],
  );

  const handlePillClick = useCallback(
    (selectedService: SERVICES) => {
      if (selectedService === service) return;
      setServiceState((prev) => selectService(selectedService, prev));
      handleSelectService(selectedService);
    },
    [service, handleSelectService],
  );

  const handleDropdownConfirm = useCallback(() => {
    if (!dropdownSelection) return;

    const selectedService = dropdownSelection as SERVICES;
    setServiceState((prev) => selectService(selectedService, prev));
    setShowDropdown(false);
    setDropdownSelection("");

    handleSelectService(dropdownSelection);
  }, [dropdownSelection, handleSelectService]);

  useEffect(() => {
    if (serviceParam && ALL_SERVICES.includes(serviceParam as SERVICES)) {
      const selectedService = serviceParam as SERVICES;
      setService(selectedService);
      setServiceState((prev) => selectService(selectedService, prev));
    }
  }, [serviceParam, setService]);

  return (
    <Guide
      id={UserGuides.SelectService}
      open
      placement="right"
      title="Select your AI tool from here"
      description="You can select your AI tool from this area. Switch to another tool when you need it."
    >
      <div className={styles.container}>
        {serviceState.visible.map((svc, index) => {
          const pill = (
            <div key={`pill-${index}`}>
              <Button
                disableRipple
                className={cx(
                  styles.serviceBtn,
                  service === svc && styles.selectedBtn,
                )}
                onClick={() => handlePillClick(svc)}
              >
                {getServiceDisplayName(svc)}
                {SERVICE_BADGES[svc as SERVICES] && (
                  <div className={styles.badge}>
                    {SERVICE_BADGES[svc as SERVICES]}
                  </div>
                )}
              </Button>
            </div>
          );
          if (svc === SERVICES.SMART_VECTORS) {
            return (
              <Guide
                key={`pill-${index}`}
                id={UserGuides.SmartVectorsNewFeature}
                open
                placement="bottom"
                title="New feature just dropped"
                description="Try out Motion Vectors."
              >
                {pill}
              </Guide>
            );
          }
          return pill;
        })}

        <Tooltip
          arrow
          placement="bottom-end"
          slotProps={{
            tooltip: {
              sx: {
                padding: "0",
                bgcolor: "transparent",
                "& .MuiTooltip-arrow": {
                  color: "#22242a",
                },
              },
            },
          }}
          open={showDropdown}
          title={
            <ClickAwayListener
              onClickAway={() => {
                setShowDropdown(false);
                setDropdownSelection("");
              }}
            >
              <div className={styles.dropdownContainer}>
                <div className={styles.dropdownLabel}>
                  What would you like to do?
                </div>

                <RadioGroup
                  aria-label="service-selector"
                  name="service-selector"
                  value={dropdownSelection}
                  onChange={(e) => setDropdownSelection(e.target.value)}
                >
                  {remainingServices.map((svc) => (
                    <FormControlLabel
                      key={svc}
                      value={svc}
                      control={
                        <Radio
                          sx={{
                            color: "#CCCCDCA6",
                            "&.Mui-checked": {
                              color: "#6565EC",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: "14px",
                            },
                          }}
                        />
                      }
                      label={
                        <span className={styles.dropdownOptionLabel}>
                          {getServiceDisplayName(svc)}
                          {SERVICE_BADGES[svc as SERVICES] && (
                            <span className={styles.badgeInDropdown}>
                              {SERVICE_BADGES[svc as SERVICES]}
                            </span>
                          )}
                        </span>
                      }
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          color: "#CCCCDC",
                          fontSize: "14px",
                          fontFamily: "Inter",
                        },
                        "& .Mui-checked + .MuiFormControlLabel-label": {
                          color: "#FFFFFFE5",
                          fontWeight: 500,
                        },
                      }}
                    />
                  ))}
                </RadioGroup>

                <Button
                  className={styles.selectBtn}
                  disabled={!dropdownSelection}
                  onClick={handleDropdownConfirm}
                >
                  Select
                </Button>
              </div>
            </ClickAwayListener>
          }
        >
          <Button
            disableRipple
            className={cx(styles.serviceBtn, styles.moreBtn)}
            onClick={() => setShowDropdown((prev) => !prev)}
          >
            More
            <KeyboardArrowDown className={styles.arrowIcon} />
          </Button>
        </Tooltip>
      </div>
    </Guide>
  );
};

export default ServiceDropdown;
